import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaLongArrowAltLeft } from "react-icons/fa";
import { quizGetDeckDetail, quizGetDeckCards, quizCreate } from "./quizApiService";
import { saveTeacherSession } from "./quizSession";
import { readLocalCardOrder, sortCardsByDeckOrder } from "../../helper/deckCardOrder";

const SECONDS_OPTIONS = [10, 20, 30, 60];

export default function QuizCardSelection() {
  const { t } = useTranslation();
  const { deckId } = useParams();
  const navigate = useNavigate();

  const [deckData, setDeckData] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCardIds, setSelectedCardIds] = useState(new Set());
  const [secondsPerAnswer, setSecondsPerAnswer] = useState(30);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const [deckRes, cardsRes] = await Promise.all([
          quizGetDeckDetail(deckId),
          quizGetDeckCards(deckId),
        ]);
        setDeckData(deckRes?.data || deckRes);
        setCards(cardsRes?.data || cardsRes || []);
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [deckId]);

  const complexityMap = useMemo(() => {
    if (!deckData?.cards) return {};
    const map = {};
    deckData.cards.forEach((card) => {
      map[card.cardId] = card.complexity;
    });
    return map;
  }, [deckData]);

  const orderedCards = useMemo(
    () => sortCardsByDeckOrder(cards, deckData?.cards, readLocalCardOrder(deckId)),
    [cards, deckData, deckId]
  );

  const allSelected = orderedCards.length > 0 && selectedCardIds.size === orderedCards.length;

  if (loading) {
    return (
      <div className="flex-1 w-full pb-28">
        <div className="max-w-[1500px] mx-auto p-5 animate-pulse">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
            <div>
              <div className="h-5 bg-gray-200 rounded w-48 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-64"></div>
            </div>
          </div>
          <div className="grid grid-cols-4 lg:grid-cols-2 md:!grid-cols-1 gap-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-3xl border p-6 bg-white">
                <div className="h-5 bg-gray-200 rounded w-3/4 mb-3"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2 mb-4"></div>
                <div className="h-3 bg-gray-200 rounded w-full"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !deckData) {
    return (
      <div className="flex-1 flex items-center justify-center px-4 py-20">
        <div className="text-center">
          <h2 className="text-xl font-bold text-black mb-2">
            {error || t("quiz.deckNotFound")}
          </h2>
          <button
            onClick={() => navigate("/quiz")}
            className="primary-btn !rounded-full h-11 !py-0 flex items-center justify-center px-7 mt-4"
          >
            {t("quiz.backToHome")}
          </button>
        </div>
      </div>
    );
  }

  const toggleCard = (cardId) => {
    setSelectedCardIds((prev) => {
      const next = new Set(prev);
      if (next.has(cardId)) {
        next.delete(cardId);
      } else {
        next.add(cardId);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedCardIds(new Set());
    } else {
      setSelectedCardIds(new Set(orderedCards.map((c) => c.cardId)));
    }
  };

  const handleGenerate = async () => {
    if (selectedCardIds.size === 0 || creating) return;
    setCreating(true);
    setCreateError(null);

    // Preserve deck card order for the cardIds we send.
    const selectedCards = orderedCards.filter((c) => selectedCardIds.has(c.cardId));
    const cardIds = selectedCards.map((c) => c.cardId);

    try {
      const { uuidQuiz, shortId } = await quizCreate({ cardIds, secondsPerAnswer });
      if (!uuidQuiz || !shortId) {
        throw new Error("Quiz creation returned an unexpected response");
      }

      saveTeacherSession({
        uuidQuiz,
        shortId,
        secondsPerAnswer,
        deckName: deckData.deckName,
        cards: selectedCards,
      });

      navigate(`/quiz/session/${shortId}/teacher`);
    } catch (err) {
      console.error("Failed to create quiz:", err);
      setCreateError(err?.response?.data?.data?.error || err?.message || "Failed to create quiz");
      setCreating(false);
    }
  };

  return (
    <div className="flex-1 w-full pb-28">
      <div className="max-w-[1500px] mx-auto p-5">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/quiz/deck/${deckId}`)}
              className="border border-blue-50 cursor-pointer rounded-full flex justify-center items-center size-10 bg-white shadow-sm"
            >
              <FaLongArrowAltLeft />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-black">
                {deckData.deckName}
              </h1>
              <p className="text-sm text-lightgray mt-0.5">
                {t("quiz.selectCardsInstruction")}
              </p>
            </div>
          </div>
          <button
            onClick={toggleAll}
            className="text-sm font-semibold text-darkblue cursor-pointer hover:underline pt-2"
          >
            {allSelected ? t("quiz.deselectAll") : t("quiz.selectAll")}
          </button>
        </div>

        {/* Card grid */}
        <div className="grid grid-cols-4 lg:grid-cols-2 md:!grid-cols-1 gap-5">
          {orderedCards.length > 0 ? (
            orderedCards.map((item) => {
              const isSelected = selectedCardIds.has(item.cardId);
              const question = item?.question?.parts?.find((part) =>
                part.contentType.includes("text")
              )?.contentBody;
              const complexity = complexityMap[item?.cardId] || 0;

              return (
                <div
                  key={item.cardId}
                  onClick={() => toggleCard(item.cardId)}
                  className={`rounded-3xl border shadow-sm p-6 bg-white cursor-pointer flex flex-col gap-2 transition-all ${
                    isSelected ? "border-darkblue border-2" : "border-blue-50 hover:border-blue-100"
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <h2 className="text-lg font-semibold text-black">
                      {item?.cardName || "Unnamed Card"}
                    </h2>
                    <div
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        isSelected
                          ? "bg-darkblue border-darkblue"
                          : "border-gray-300"
                      }`}
                    >
                      {isSelected && (
                        <svg
                          className="w-3.5 h-3.5 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-between gap-2 mt-1">
                    <span className="text-xs font-medium px-2 py-1 bg-blue-50 text-darkblue rounded-md capitalize">
                      {t("complexity")}: {complexity}
                    </span>
                    <span className="text-xs font-medium px-2 py-1 bg-gray-50 text-gray-600 rounded-md capitalize">
                      {t("answerType")}: {item?.answerType?.toLowerCase() || "N/A"}
                    </span>
                  </div>

                  <hr className="my-2 border-blue-50" />

                  <p className="text-sm text-lightgray line-clamp-2">
                    {question || "N/A"}
                  </p>
                </div>
              );
            })
          ) : (
            <div className="col-span-4 text-center text-lightgray py-10">
              {t("nocard")}
            </div>
          )}
        </div>
      </div>

      {/* Fixed bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-blue-50 p-4 z-20 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
        <div className="max-w-[1500px] mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="bg-darkblue text-white text-sm font-bold px-3 py-1 rounded-full shadow-sm">
              {selectedCardIds.size}
            </span>
            <span className="regular-text text-lightgray">
              {t("quiz.cardsSelected")}
            </span>
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            {/* Seconds per question */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-lightgray whitespace-nowrap">
                {t("quiz.secondsPerQuestion") || "Seconds / question"}
              </span>
              <div className="flex items-center bg-gray-50 rounded-full border border-gray-200 p-1">
                {SECONDS_OPTIONS.map((sec) => (
                  <button
                    key={sec}
                    onClick={() => setSecondsPerAnswer(sec)}
                    className={`px-3 py-1 rounded-full text-sm font-semibold transition-colors ${
                      secondsPerAnswer === sec
                        ? "bg-darkblue text-white"
                        : "text-gray-600 hover:text-black"
                    }`}
                  >
                    {sec}s
                  </button>
                ))}
              </div>
            </div>

            {createError && (
              <span className="text-sm text-red-500 font-medium">{createError}</span>
            )}

            <button
              onClick={handleGenerate}
              disabled={selectedCardIds.size === 0 || creating}
              className={`primary-btn !rounded-full h-11 !py-0 flex items-center justify-center px-7 text-base font-semibold ${
                selectedCardIds.size === 0 || creating ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {creating ? (t("quiz.creating") || "Creating...") : t("quiz.generateQuiz")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
